<template>
  <div class="account-settings">
    <!-- <q-list separator> -->
    <q-list>
      <!-- Profile Header -->
      <q-item-label header class="text-weight-medium text-grey-8">
        <q-icon name="account_circle" class="q-mr-sm" />
        {{$t('Profile')}}
      </q-item-label>

      <!-- User Info -->
      <q-item>
        <q-item-section avatar>
          <q-avatar size="md">
            <img v-if="userData.avatar" :src="userData.avatar" />
            <q-icon v-else name="person" size="md" />
          </q-avatar>
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8 text-weight-medium">
            {{ userData.first_name }} {{ userData.last_name }}
          </q-item-label>
          <q-item-label caption class="text-grey-6">
            {{ userData.email }}
          </q-item-label>
        </q-item-section>
      </q-item>

      <!-- Login Method -->
      <q-item v-if="loginMethod !== 'keycloak'">
        <q-item-section avatar>
          <q-icon name="login" color="grey-6" size="md" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8">
            {{$t('Login Method')}}
          </q-item-label>
          <q-item-label caption>
            <q-chip 
              :icon="loginMethodIcon" 
              :color="loginMethodColor"
              text-color="white"
              size="sm"
            >
              {{ loginMethodLabel }}
            </q-chip>
          </q-item-label>
        </q-item-section>
      </q-item>

      <q-separator class="q-my-md" />

      <!-- Account Actions -->
      <q-item-label header class="text-weight-medium text-grey-8">
        <q-icon name="settings" class="q-mr-sm" />
        {{$t('Account Settings')}}
      </q-item-label>

      <!-- Manage Account -->
      <q-item 
        clickable 
        v-ripple
        @click="manageAccount"
      >
        <q-item-section avatar>
          <q-icon name="manage_accounts" color="grey-6" size="md" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8 text-weight-medium">
            {{$t('Manage Account')}}
          </q-item-label>
          <q-item-label caption class="text-grey-6">
            {{$t('Change password, email, and security')}}
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          <q-icon name="open_in_new" color="grey-4" />
        </q-item-section>
      </q-item>

      <!-- Linked Accounts -->
      <q-item 
        clickable 
        v-ripple
        @click="linkedAccounts"
      >
        <q-item-section avatar>
          <q-icon name="link" color="grey-6" size="md" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-grey-8 text-weight-medium">
            {{$t('Linked Accounts')}}
          </q-item-label>
          <q-item-label caption class="text-grey-6">
            {{$t('Connect social logins')}}
          </q-item-label>
        </q-item-section>
        <q-item-section side>
          <!-- <q-icon name="chevron_right" color="grey-4" /> -->
           <q-icon name="open_in_new" color="grey-4" />
        </q-item-section>
      </q-item>

      <q-separator class="q-my-md" />

      <!-- Sign Out -->
      <q-item 
        clickable 
        v-ripple
        @click="signOut"
      >
        <q-item-section avatar>
          <q-icon name="logout" color="negative" size="md" />
        </q-item-section>
        <q-item-section>
          <q-item-label class="text-negative text-weight-medium">
            {{$t('Sign Out')}}
          </q-item-label>
        </q-item-section>
      </q-item>
    </q-list>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { useAuth } from '@/common/composables/useAuth'
import { useAppStore } from '@/common/composables/useAppStore'
import { useLanguage } from '@/common/composables/useLanguage'

const { keycloak, logout } = useAuth()
const { userData } = useAppStore()
const { currentLocaleShort } = useLanguage()



// Login method detection
const loginMethod = computed(() => {
  if (!keycloak?.tokenParsed) return 'keycloak'
  const idp = keycloak.tokenParsed.identity_provider || keycloak.tokenParsed.idp
  return idp || 'keycloak'
})

const loginMethodIcon = computed(() => {
  const icons = {
    google: 'img:https://www.google.com/favicon.ico',
    github: 'img:https://github.githubassets.com/favicons/favicon.svg',
    keycloak: 'key'
  }
  return icons[loginMethod.value] || 'login'
})

const loginMethodColor = computed(() => {
  const colors = {
    google: 'red',
    github: 'grey-9',
    keycloak: 'primary'
  }
  return colors[loginMethod.value] || 'grey'
})

const loginMethodLabel = computed(() => {
  const labels = {
    google: 'Google',
    github: 'GitHub',
    keycloak: 'Email & Password'
  }
  return labels[loginMethod.value] || 'Unknown'
})

const manageAccount = () => {
  if (keycloak?.createAccountUrl) {
    window.open(keycloak.createAccountUrl({ locale: currentLocaleShort.value }), '_blank')
  }
}

const linkedAccounts = () => {
  if (keycloak?.createAccountUrl) {
    const accountUrl = keycloak.createAccountUrl({ locale: currentLocaleShort.value })
    window.open(`${accountUrl}#/security/linked-accounts`, '_blank')
  }
}

const signOut = () => {
  logout()
}
</script>

<style lang="scss" scoped>
.text-grey-8 {
  body.body--dark & {
    color: rgba(255, 255, 255, 0.87) !important;
  }
}

.text-grey-6 {
  body.body--dark & {
    color: rgba(255, 255, 255, 0.6) !important;
  }
}
</style>